// The Supabase data source: implements the SAME `PortalDataSource` contract as
// the fixtures adapter, backed by RLS-scoped Postgres queries.
//
// This adapter is selected ONLY when VITE_SUPABASE_URL is set (see ./index).
// Fixtures stay the default, so the existing tests and UI are untouched.
//
// SECURITY: every query relies on Row Level Security for scoping — there are no
// manual `org_id = …` / `account_id = …` filters here. RLS (see the migration,
// task 622) is the access boundary: the publishable/anon key carries the signed
// -in caller's JWT, and Postgres returns only the rows that caller may read.
// Cross-org isolation is verified later by task 627 + a live security review.
//
// Column names are snake_case on the wire; these mappers translate to the
// camelCase domain types (601). Children come back as `ServerChild` — the
// `child` table carries id + owner only (zero PII server-side).

import type { PostgrestError, SupabaseClient } from "@supabase/supabase-js";
import type {
  Account,
  Assignment,
  ChildLink,
  Consent,
  ConsentScopeKey,
  Id,
  Organisation,
  OrganisationType,
  MemberInvitation,
  Membership,
  MembershipRole,
  License,
  Seat,
  SeatStatus,
  SessionSummary,
  SkillStepState,
  SkillSummary,
  StepStage,
  SupportProfile,
  ChildRelation,
  AssignmentStatus,
} from "../domain/types";
import { normaliseNote } from "../domain/assignmentNote";
import type {
  PortalDataSource,
  ServerChild,
  OrgOverview,
  OrgRosterEntry,
  OrgConsentEntry,
  OrgActivityEntry,
  OrgActivityKind,
  OrgMemberEntry,
  SharingStatus,
  AccessCode,
  BillingSummary,
  BulkLicenceResult,
  BulkLicenceStatus,
  ChildAccess,
  OrgChildStaff,
  ChildActivity,
  ChildAdult,
  ChildLicence,
  ChildSuggestion,
  ConsoleOrg,
  FamilyLicenceCheck,
  FamilyLicenceCheckStatus,
  HomeNote,
  Invoice,
  InvoicePreview,
  InvoiceStatus,
  OrgAnalytics,
  OrgChildEntry,
  OrgKind,
  OrgLifecycle,
  OrgProfile,
} from "./types";
import type { LinkRole } from "../domain/types";
import type { Sponsorship } from "../domain/sponsorship";

/**
 * Error thrown by every write in this adapter. Carries the operation name and,
 * when the failure came from Postgres, the SQLSTATE `code` the `security definer`
 * RPCs raise (42501 not-authorized, P0001 rule violation e.g. claimed/expired/
 * no-seats, P0002 not-found). The UI maps on `code`, never on message text.
 */
export class PortalWriteError extends Error {
  constructor(
    readonly op: string,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "PortalWriteError";
  }
}

// --- Raw row shapes (snake_case, as returned by PostgREST) -------------------

interface OrganisationRow {
  id: string;
  name: string;
  type: string;
  seat_limit: number;
  created_at: string;
}
interface AccountRow {
  id: string;
  email: string;
  org_id: string | null;
  created_at: string;
}
interface MembershipRow {
  account_id: string;
  org_id: string;
  role: string;
}
interface ChildRow {
  id: string;
  owner_account_id: string;
}
interface ChildLinkRow {
  child_id: string;
  account_id: string;
  relation: string;
  consent_id: string | null;
  scope: string[];
  role?: string | null;
  label?: string | null;
  created_at?: string | null;
}
interface ConsentRow {
  id: string;
  scope: string[];
  granted_by: string;
  granted_at: string;
  revoked_at: string | null;
}
interface SessionSummaryRow {
  child_id: string;
  game_id: string;
  duration_seconds: number;
  /** Nullable since migration 20261022000007: "not measured" is not zero. */
  accuracy: number | null;
  at: string;
}
interface SkillSummaryRow {
  child_id: string;
  domain: string;
  level: number;
  updated_at: string;
}
interface AssignmentRow {
  id: string;
  child_id: string;
  assigned_by_account_id: string;
  target: string;
  status: string;
  at: string;
  note?: string | null;
  seen_at?: string | null;
  updated_at?: string | null;
}
interface SkillStepStateRow {
  child_id: string;
  skill_id: string;
  step: number;
  stage: string;
  explore_on: string | null;
  help_on: string | null;
  own_on: string | null;
  steady_on: string | null;
  places_on: string | null;
  last_practised_on: string | null;
  updated_at: string;
}
interface SupportProfileRow {
  child_id: string;
  captions: boolean | null;
  reduced_motion: boolean | null;
  high_contrast: boolean | null;
  calm_mode: boolean | null;
  tap_only: boolean | null;
  language: string | null;
  updated_at: string;
}
interface LicenseRow {
  org_id: string;
  seats_used: number;
  seats_total: number;
  plan: string;
  period: string;
}
interface SeatRow {
  org_id: string;
  account_id: string;
  status: string;
}
interface MemberInvitationRow {
  id: string;
  org_id: string;
  email: string;
  role: string;
  invited_by: string;
  expires_at: string;
  created_at: string;
}

// --- Row → domain mappers ----------------------------------------------------

const toOrganisation = (r: OrganisationRow): Organisation => ({
  id: r.id,
  name: r.name,
  type: r.type as OrganisationType,
  seatLimit: r.seat_limit,
  createdAt: r.created_at,
});
const toAccount = (r: AccountRow): Account => ({
  id: r.id,
  email: r.email,
  ...(r.org_id ? { orgId: r.org_id } : {}),
  createdAt: r.created_at,
});
const toMembership = (r: MembershipRow): Membership => ({
  accountId: r.account_id,
  orgId: r.org_id,
  role: r.role as MembershipRole,
});
const toChild = (r: ChildRow): ServerChild => ({
  id: r.id,
  ownerAccountId: r.owner_account_id,
});
const toChildLink = (r: ChildLinkRow): ChildLink => ({
  childId: r.child_id,
  accountId: r.account_id,
  relation: r.relation as ChildRelation,
  ...(r.consent_id ? { consentId: r.consent_id } : {}),
  scope: (r.scope ?? []) as ConsentScopeKey[],
  ...(r.role !== undefined ? { role: (r.role as LinkRole | null) ?? null } : {}),
  ...(r.label !== undefined ? { label: r.label ?? null } : {}),
  ...(r.created_at ? { createdAt: r.created_at } : {}),
});
const toConsent =(r: ConsentRow): Consent => ({
  id: r.id,
  scope: (r.scope ?? []) as ConsentScopeKey[],
  grantedBy: r.granted_by,
  grantedAt: r.granted_at,
  ...(r.revoked_at ? { revokedAt: r.revoked_at } : {}),
});
const toSessionSummary = (r: SessionSummaryRow): SessionSummary => ({
  childId: r.child_id,
  gameId: r.game_id,
  durationSeconds: r.duration_seconds,
  accuracy: typeof r.accuracy === "number" ? r.accuracy : null,
  at: r.at,
});
const toSkillSummary = (r: SkillSummaryRow): SkillSummary => ({
  childId: r.child_id,
  domain: r.domain,
  level: r.level,
  updatedAt: r.updated_at,
});
const toAssignment = (r: AssignmentRow): Assignment => ({
  id: r.id,
  childId: r.child_id,
  assignedByAccountId: r.assigned_by_account_id,
  target: r.target,
  status: r.status as AssignmentStatus,
  at: r.at,
  ...(r.note != null ? { note: r.note } : {}),
  ...(r.seen_at != null ? { seenAt: r.seen_at } : {}),
  ...(r.updated_at != null ? { updatedAt: r.updated_at } : {}),
});
/** Postgres `date` comes back as "YYYY-MM-DD"; keep only that part defensively. */
const toIsoDate = (v: string | null): string | null =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : null;
const toSkillStepState = (r: SkillStepStateRow): SkillStepState => ({
  childId: r.child_id,
  skillId: r.skill_id,
  step: r.step,
  stage: r.stage as StepStage,
  exploreOn: toIsoDate(r.explore_on),
  helpOn: toIsoDate(r.help_on),
  ownOn: toIsoDate(r.own_on),
  steadyOn: toIsoDate(r.steady_on),
  placesOn: toIsoDate(r.places_on),
  lastPractisedOn: toIsoDate(r.last_practised_on),
  updatedAt: r.updated_at,
});
const toSupportProfile = (r: SupportProfileRow): SupportProfile => ({
  childId: r.child_id,
  captions: r.captions,
  reducedMotion: r.reduced_motion,
  highContrast: r.high_contrast,
  calmMode: r.calm_mode,
  tapOnly: r.tap_only,
  language: r.language,
  updatedAt: r.updated_at,
});
const toLicense = (r: LicenseRow): License => ({
  orgId: r.org_id,
  seatsUsed: r.seats_used,
  seatsTotal: r.seats_total,
  plan: r.plan,
  period: r.period,
});
const toSeat = (r: SeatRow): Seat => ({
  orgId: r.org_id,
  accountId: r.account_id,
  status: r.status as SeatStatus,
});
// Admin aggregate RPC rows. `child_ref` is the opaque org-scoped ref the RPC
// computes — the child UUID never crosses this boundary.
interface OrgOverviewRow {
  org_name: string;
  org_type: string;
  seats_total: number;
  seats_used: number;
  member_count: number;
  admin_count: number;
  linked_child_count: number;
  pending_invite_count: number;
}
interface OrgMemberRow {
  account_id: string;
  email: string;
  role: string;
  seat_status: string | null;
  joined_at: string;
}
interface OrgRosterRow {
  child_ref: string;
  supported_by: string[] | null;
  sharing_status: string;
  last_change: string | null;
}
interface OrgConsentRow {
  consent_id: string;
  child_ref: string;
  member_email: string;
  scope: string[] | null;
  status: string;
  granted_at: string;
  revoked_at: string | null;
}
interface OrgActivityRow {
  id: string;
  kind: string;
  text: string;
  at: string;
}

const toOrgOverview = (r: OrgOverviewRow): OrgOverview => ({
  orgName: r.org_name,
  orgType: r.org_type,
  seatsTotal: r.seats_total,
  seatsUsed: r.seats_used,
  memberCount: r.member_count,
  adminCount: r.admin_count,
  linkedChildCount: r.linked_child_count,
  pendingInviteCount: r.pending_invite_count,
});
const toOrgMemberEntry = (r: OrgMemberRow): OrgMemberEntry => ({
  accountId: r.account_id,
  email: r.email,
  role: r.role as MembershipRole,
  seatStatus: (r.seat_status as "active" | "pending" | null) ?? null,
  joinedAt: r.joined_at,
});
const toOrgRosterEntry = (r: OrgRosterRow): OrgRosterEntry => ({
  childRef: r.child_ref,
  supportedBy: r.supported_by ?? [],
  sharingStatus: r.sharing_status as SharingStatus,
  lastChange: r.last_change,
});
const toOrgConsentEntry = (r: OrgConsentRow): OrgConsentEntry => ({
  consentId: r.consent_id,
  childRef: r.child_ref,
  memberEmail: r.member_email,
  scope: (r.scope ?? []) as ConsentScopeKey[],
  status: r.status as SharingStatus,
  grantedAt: r.granted_at,
  revokedAt: r.revoked_at,
});
const toOrgActivityEntry = (r: OrgActivityRow): OrgActivityEntry => ({
  id: r.id,
  kind: r.kind as OrgActivityKind,
  text: r.text,
  at: r.at,
});

/**
 * A `family_sponsorship` row. Adult email + dates only — the table references no
 * child, and `org_id` / `granted_by` / `revoked_by` are deliberately NOT read:
 * the screen has no use for them and they would only widen what an admin holds.
 */
interface FamilySponsorshipRow {
  id: string;
  email: string;
  ends_at: string;
  granted_at: string;
  claimed_at: string | null;
  revoked_at: string | null;
}

/** Columns `listSponsorships` asks for. One constant so the test can assert it. */
export const SPONSORSHIP_COLUMNS =
  "id, email, ends_at, granted_at, claimed_at, revoked_at";

const toSponsorship = (r: FamilySponsorshipRow): Sponsorship => ({
  id: r.id,
  email: r.email,
  endsAt: r.ends_at,
  grantedAt: r.granted_at,
  claimedAt: r.claimed_at,
  revokedAt: r.revoked_at,
});

const toMemberInvitation = (r: MemberInvitationRow): MemberInvitation => ({
  id: r.id,
  orgId: r.org_id,
  email: r.email,
  role: r.role as MembershipRole,
  invitedBy: r.invited_by,
  expiresAt: r.expires_at,
  createdAt: r.created_at,
});

// --- Adapter -----------------------------------------------------------------

/** Assignment columns read and returned on insert (note/seen_at/updated_at: migration 20261022000007). */
const ASSIGNMENT_COLUMNS =
  "id, child_id, assigned_by_account_id, target, status, at, note, seen_at, updated_at";

/**
 * Runs `select(columns)` on `table` and maps each row. RLS scopes the result to
 * the signed-in caller; a PostgREST error is surfaced as a thrown Error so the
 * UI's normal error path fires rather than silently rendering empty data.
 */
async function selectAll<Row, T>(
  client: SupabaseClient,
  table: string,
  columns: string,
  map: (row: Row) => T,
): Promise<T[]> {
  const { data, error } = await client.from(table).select(columns);
  if (error) {
    throw new Error(`[supabase] failed to read "${table}": ${error.message}`);
  }
  return ((data ?? []) as Row[]).map(map);
}

/** Wraps a PostgREST error in a PortalWriteError, preserving the SQLSTATE code. */
function rpcError(op: string, error: PostgrestError): PortalWriteError {
  return new PortalWriteError(op, error.message, error.code);
}

/**
 * Calls a `security definer` RPC and returns its payload. A PostgREST error is
 * surfaced as a {@link PortalWriteError} carrying the SQLSTATE `code` the RPCs
 * raise (42501 / P0001 / P0002), so the UI maps on the code, not the message.
 *
 * `args` keys MUST match the SQL parameter names byte-for-byte (p_child,
 * p_scope, p_code, p_viewer, p_org, p_account, p_email, p_role) — that mapping
 * is the load-bearing contract with the migration.
 */
async function callRpc<T>(
  client: SupabaseClient,
  fn: string,
  args: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await client.rpc(fn, args);
  if (error) throw rpcError(fn, error);
  return data as T;
}

/**
 * Builds a {@link PortalDataSource} backed by a Supabase client. The client is
 * injected (not imported) so it can be unit-tested with a mock and so the
 * singleton wiring lives in ./index.
 */
export function createSupabaseDataSource(
  client: SupabaseClient,
): PortalDataSource {
  return {
    organisations: () =>
      selectAll<OrganisationRow, Organisation>(
        client,
        "organisation",
        "id, name, type, seat_limit, created_at",
        toOrganisation,
      ),
    accounts: () =>
      selectAll<AccountRow, Account>(
        client,
        "account",
        "id, email, org_id, created_at",
        toAccount,
      ),
    memberships: () =>
      selectAll<MembershipRow, Membership>(
        client,
        "membership",
        "account_id, org_id, role",
        toMembership,
      ),
    children: () =>
      selectAll<ChildRow, ServerChild>(
        client,
        "child",
        "id, owner_account_id",
        toChild,
      ),
    childLinks: async () => {
      // role / label / created_at arrive with migration 20261022000008. A
      // backend without them answers 42703 (undefined column); fall back to the
      // original columns rather than take every caregiver screen down.
      const { data, error } = await client
        .from("child_link")
        .select(CHILD_LINK_COLUMNS);
      if (!error) return ((data ?? []) as unknown as ChildLinkRow[]).map(toChildLink);
      if (error.code !== "42703") {
        throw new Error(`[supabase] failed to read "child_link": ${error.message}`);
      }
      return selectAll<ChildLinkRow, ChildLink>(
        client,
        "child_link",
        "child_id, account_id, relation, consent_id, scope",
        toChildLink,
      );
    },
    consents: () =>
      selectAll<ConsentRow, Consent>(
        client,
        "consent",
        "id, scope, granted_by, granted_at, revoked_at",
        toConsent,
      ),
    sessionSummaries: () =>
      selectAll<SessionSummaryRow, SessionSummary>(
        client,
        "session_summary",
        "child_id, game_id, duration_seconds, accuracy, at",
        toSessionSummary,
      ),
    skillSummaries: () =>
      selectAll<SkillSummaryRow, SkillSummary>(
        client,
        "skill_summary",
        "child_id, domain, level, updated_at",
        toSkillSummary,
      ),
    assignments: () =>
      selectAll<AssignmentRow, Assignment>(
        client,
        "assignment",
        ASSIGNMENT_COLUMNS,
        toAssignment,
      ),
    stepStates: () =>
      selectAll<SkillStepStateRow, SkillStepState>(
        client,
        "skill_step_state",
        "child_id, skill_id, step, stage, explore_on, help_on, own_on, steady_on, places_on, last_practised_on, updated_at",
        toSkillStepState,
      ),
    supportProfiles: () =>
      selectAll<SupportProfileRow, SupportProfile>(
        client,
        "support_profile",
        "child_id, captions, reduced_motion, high_contrast, calm_mode, tap_only, language, updated_at",
        toSupportProfile,
      ),
    licenses: () =>
      selectAll<LicenseRow, License>(
        client,
        "license",
        "org_id, seats_used, seats_total, plan, period",
        toLicense,
      ),
    seats: () =>
      selectAll<SeatRow, Seat>(
        client,
        "seat",
        "org_id, account_id, status",
        toSeat,
      ),
    memberInvitations: () =>
      selectAll<MemberInvitationRow, MemberInvitation>(
        client,
        "member_invitation",
        "id, org_id, email, role, invited_by, expires_at, created_at",
        toMemberInvitation,
      ),

    // --- Writes: privileged RPCs (same functions the mobile app calls) --------
    generateLinkCode: (childId, scope) =>
      callRpc<string>(client, "generate_link_code", {
        p_child: childId,
        p_scope: scope ?? ["progress"],
      }),
    claimLinkCode: (code) =>
      callRpc<Id>(client, "claim_link_code", { p_code: code }),
    grantConsent: (childId, viewerAccountId, scope) =>
      callRpc<Id>(client, "grant_consent", {
        p_child: childId,
        p_viewer: viewerAccountId,
        p_scope: scope,
      }),
    revokeConsent: (childId, viewerAccountId, scope) =>
      callRpc<void>(client, "revoke_consent", {
        p_child: childId,
        p_viewer: viewerAccountId,
        p_scope: scope,
      }),
    purgeSharedData: (childId, viewerAccountId) =>
      callRpc<void>(client, "purge_shared_data", {
        p_child: childId,
        p_viewer: viewerAccountId,
      }),
    allocateSeat: (orgId, accountId) =>
      callRpc<void>(client, "allocate_seat", {
        p_org: orgId,
        p_account: accountId,
      }),
    revokeSeat: (orgId, accountId) =>
      callRpc<void>(client, "revoke_seat", {
        p_org: orgId,
        p_account: accountId,
      }),
    inviteMember: (orgId, email, role) =>
      callRpc<Id>(client, "invite_member", {
        p_org: orgId,
        p_email: email,
        p_role: role,
      }),

    // --- Grouped practice ideas + their history (migration 20261023000010) ----
    // (Suggestions are written ONLY through create_suggestions: migration 0015
    // dropped assignment_writer_write, so a direct insert would be refused.)
    createSuggestions: (childId, targets, note) =>
      callRpc<Id>(client, "create_suggestions", {
        p_child: childId,
        p_targets: [...targets],
        p_note: normaliseNote(note),
      }),
    childSuggestions: async (childId) =>
      (
        (await callRpc<ChildSuggestionRow[] | null>(client, "child_suggestions", {
          p_child: childId,
        })) ?? []
      ).map(toChildSuggestion),
    cancelInvite: async (invitationId) => {
      const { error } = await client
        .from("member_invitation")
        .delete()
        .eq("id", invitationId);
      if (error) throw rpcError("cancelInvite", error);
    },

    // --- Organisation onboarding ---------------------------------------------
    createOrganisation: (name, type, accessCode) =>
      callRpc<Id>(client, "create_organisation", {
        p_name: name,
        p_type: type,
        p_code: accessCode,
      }),
    acceptMemberInvitations: async () =>
      (await callRpc<number | null>(client, "accept_member_invitations", {})) ??
      0,
    setMemberRole: (orgId, accountId, role) =>
      callRpc<void>(client, "set_member_role", {
        p_org: orgId,
        p_account: accountId,
        p_role: role,
      }),
    updateOrganisation: (orgId, name, type) =>
      callRpc<void>(client, "update_organisation", {
        p_org: orgId,
        p_name: name,
        p_type: type,
      }),
    // Atomic server-side: frees the seat, cancels pending invites and drops the
    // membership in ONE transaction. The previous two-call version could leave a
    // seat allocated to a non-member when the second call failed.
    removeMember: (orgId, accountId) =>
      callRpc<void>(client, "remove_member", {
        p_org: orgId,
        p_account: accountId,
      }),

    // --- Admin aggregate reads -----------------------------------------------
    //
    // These return OPAQUE child refs, never child UUIDs. See ./types.ts.
    orgOverview: async (orgId) => {
      // The RPC RETURNS TABLE, so PostgREST hands back an array of (here, one) row.
      const rows = await callRpc<OrgOverviewRow[] | null>(
        client,
        "org_overview",
        { p_org: orgId },
      );
      const row = rows?.[0];
      return row ? toOrgOverview(row) : null;
    },
    orgMembers: async (orgId) =>
      (
        (await callRpc<OrgMemberRow[] | null>(client, "org_members", {
          p_org: orgId,
        })) ?? []
      ).map(toOrgMemberEntry),
    orgRoster: async (orgId) =>
      (
        (await callRpc<OrgRosterRow[] | null>(client, "org_roster_summary", {
          p_org: orgId,
        })) ?? []
      ).map(toOrgRosterEntry),
    orgConsentLog: async (orgId) =>
      (
        (await callRpc<OrgConsentRow[] | null>(client, "org_consent_log", {
          p_org: orgId,
        })) ?? []
      ).map(toOrgConsentEntry),
    orgActivity: async (orgId, limit = 12) => {
      const { data, error } = await client
        .from("org_activity")
        .select("id, kind, text, at")
        .eq("org_id", orgId)
        .order("at", { ascending: false })
        .limit(limit);
      // A PortalWriteError (not a bare Error) so the UI can branch on the
      // SQLSTATE exactly as it does for every other admin read, rather than
      // falling through to the generic "something went wrong" copy.
      if (error) throw rpcError("orgActivity", error);
      return ((data ?? []) as OrgActivityRow[]).map(toOrgActivityEntry);
    },

    // --- Family sponsorship ---------------------------------------------------
    //
    // READS go direct: `family_sponsorship_admin_read` already restricts a
    // select to `app_is_org_admin(org_id)`, so RLS is the access boundary here
    // exactly as everywhere else in this adapter. The `.eq("org_id", …)` is a
    // narrowing filter for the screen's benefit, not the security control — the
    // same shape `orgActivity` uses.
    //
    // WRITES go through RPCs because they must: the table carries NO
    // insert/update/delete policy, on purpose. A direct insert would bypass the
    // family-allowance check inside grant_family_sponsorship.
    listSponsorships: async (orgId) => {
      const { data, error } = await client
        .from("family_sponsorship")
        .select(SPONSORSHIP_COLUMNS)
        .eq("org_id", orgId)
        .order("granted_at", { ascending: false });
      // A PortalWriteError (not a bare Error) so the screen can branch on the
      // SQLSTATE — 42501 means "you are not an admin of this org", which is a
      // different sentence from "the network is down".
      if (error) throw rpcError("listSponsorships", error);
      return ((data ?? []) as unknown as FamilySponsorshipRow[]).map(
        toSponsorship,
      );
    },
    getFamilyAllowance: async (orgId) => {
      const { data, error } = await client
        .from("license")
        .select("org_id, family_allowance")
        .eq("org_id", orgId);
      if (error) throw rpcError("getFamilyAllowance", error);
      const rows = (data ?? []) as unknown as {
        org_id: string;
        family_allowance: number | null;
      }[];
      // No licence row, or a null column, means no allowance — the same answer
      // grant_family_sponsorship gives, so the UI and the server agree.
      return rows[0]?.family_allowance ?? 0;
    },
    grantSponsorship: (orgId, email, endsAt) =>
      callRpc<Id>(client, "grant_family_sponsorship", {
        p_org: orgId,
        p_email: email,
        p_ends_at: endsAt,
      }),
    revokeSponsorship: (id) =>
      callRpc<void>(client, "revoke_family_sponsorship", { p_id: id }),

    // --- Family licences, billing, kids, analytics (migration 0008) ---------
    orgProfile: async (orgId) => {
      const { data, error } = await client
        .from("organisation")
        .select("id, kind, status")
        .eq("id", orgId);
      if (error) throw rpcError("orgProfile", error);
      const row = ((data ?? []) as unknown as { kind?: string | null; status?: string | null }[])[0];
      return {
        kind: row?.kind === "individual" ? "individual" : "organisation",
        status: toLifecycle(row?.status),
      };
    },
    checkFamilyLicence: async (orgId, email) => {
      const rows = await callRpc<{ status: string; remaining: number }[] | null>(
        client,
        "check_family_licence",
        { p_org: orgId, p_email: email },
      );
      const row = rows?.[0];
      return {
        status: (row?.status ?? "no_allowance") as FamilyLicenceCheckStatus,
        remaining: num(row?.remaining),
      } satisfies FamilyLicenceCheck;
    },
    extendSponsorship: (id, endsAt) =>
      callRpc<void>(client, "extend_family_sponsorship", {
        p_id: id,
        p_ends_at: endsAt,
      }),
    orgBillingSummary: async (orgId) => {
      const rows = await callRpc<BillingSummaryRow[] | null>(
        client,
        "org_billing_summary",
        { p_org: orgId },
      );
      const row = rows?.[0];
      return row ? toBillingSummary(row) : null;
    },
    listInvoices: async (orgId) => {
      const { data, error } = await client
        .from("invoice")
        .select(INVOICE_COLUMNS)
        .eq("org_id", orgId)
        .order("period_start", { ascending: false });
      if (error) throw rpcError("listInvoices", error);
      return ((data ?? []) as unknown as InvoiceRow[]).map(toInvoice);
    },
    orgChildren: async (orgId) =>
      (
        (await callRpc<OrgChildRow[] | null>(client, "org_children", {
          p_org: orgId,
        })) ?? []
      ).map(toOrgChildEntry),
    setOrgChildArchived: (orgId, childRef, archived) =>
      callRpc<void>(client, "set_org_child_archived", {
        p_org: orgId,
        p_child_ref: childRef,
        p_archived: archived,
      }),
    endOrgSupport: async (orgId, childRef) =>
      num(
        await callRpc<number | null>(client, "end_org_support", {
          p_org: orgId,
          p_child_ref: childRef,
        }),
      ),
    orgAnalytics: async (orgId) =>
      toOrgAnalytics(
        await callRpc<Record<string, unknown> | null>(client, "org_analytics", {
          p_org: orgId,
        }),
      ),

    // --- Licence tools, organisation consent + staff (0012, 0013) -------------
    grantSponsorshipsBulk: async (orgId, emails, endsAt) =>
      (
        (await callRpc<BulkLicenceRow[] | null>(client, "grant_family_sponsorships_bulk", {
          p_org: orgId,
          p_emails: [...emails],
          p_ends_at: endsAt,
        })) ?? []
      ).map(toBulkLicenceResult),
    setSponsorshipEndDates: async (orgId, ids, endsAt) =>
      num(
        await callRpc<number | null>(client, "set_sponsorship_end_dates", {
          p_org: orgId,
          p_ids: [...ids],
          p_ends_at: endsAt,
        }),
      ),
    changeSponsorshipEmail: (id, email) =>
      callRpc<Id>(client, "change_sponsorship_email", { p_id: id, p_email: email }),
    orgChildStaff: async (orgId, childRef) =>
      (
        (await callRpc<OrgChildStaffRow[] | null>(client, "org_child_staff", {
          p_org: orgId,
          p_child_ref: childRef,
        })) ?? []
      ).map(toOrgChildStaff),
    assignChildStaff: (orgId, childRef, accountId, role) =>
      callRpc<void>(client, "assign_child_staff", {
        p_org: orgId,
        p_child_ref: childRef,
        p_account: accountId,
        p_role: role,
      }),
    unassignChildStaff: (orgId, childRef, accountId) =>
      callRpc<void>(client, "unassign_child_staff", {
        p_org: orgId,
        p_child_ref: childRef,
        p_account: accountId,
      }),
    childAccess: async (childId) => {
      const rows = await callRpc<{ premium: boolean | null; source: string | null }[] | null>(
        client,
        "child_access",
        { p_child: childId },
      );
      const row = rows?.[0];
      const source = row?.source === "store" || row?.source === "licence" ? row.source : "none";
      // Fail closed: no row reads as no Premium, never the other way round.
      return { premium: row?.premium === true && source !== "none", source } satisfies ChildAccess;
    },

    // --- Owner console --------------------------------------------------------
    amIPlatformAdmin: async () => {
      try {
        return (await callRpc<boolean | null>(client, "am_i_platform_admin", {})) === true;
      } catch {
        // No function yet, no session, any failure: the console stays hidden.
        return false;
      }
    },
    consoleOrgs: async () =>
      ((await callRpc<ConsoleOrgRow[] | null>(client, "console_orgs", {})) ?? []).map(
        toConsoleOrg,
      ),
    consoleAccessCodes: async () =>
      (
        (await callRpc<AccessCodeRow[] | null>(client, "console_access_codes", {})) ?? []
      ).map(toAccessCode),
    consoleCreateAccessCode: (input) =>
      callRpc<string>(client, "console_create_access_code", {
        p_label: input.label,
        p_kind: input.kind,
        p_seats: input.seats,
        p_family_allowance: input.familyAllowance,
        p_family_price: input.familyPrice,
        p_family_retail_price: input.familyRetailPrice,
        p_sst_rate: input.sstRate,
        p_currency: input.currency,
        p_max_uses: input.maxUses,
        p_expires_at: input.expiresAt,
      }),
    consoleLicenceTerms: async (orgId) => {
      const { data, error } = await client
        .from("license")
        .select(LICENCE_TERMS_COLUMNS)
        .eq("org_id", orgId);
      if (error) throw rpcError("consoleLicenceTerms", error);
      const r = ((data ?? []) as unknown as Record<string, unknown>[])[0] ?? {};
      return {
        seats: r.seats_total == null ? null : num(r.seats_total),
        familyAllowance: r.family_allowance == null ? null : num(r.family_allowance),
        familyPrice: r.family_price_monthly == null ? null : num(r.family_price_monthly),
        familyRetailPrice: r.family_retail_price_monthly == null ? null : num(r.family_retail_price_monthly),
        sstRate: r.sst_rate == null ? null : num(r.sst_rate),
        currency: typeof r.currency === "string" ? r.currency : null,
        billingEmail: typeof r.billing_email === "string" ? r.billing_email : null,
        contractEnd: toIsoDate(typeof r.contract_end === "string" ? r.contract_end : null),
      };
    },
    consoleSetLicence: (orgId, terms) =>
      callRpc<void>(client, "console_set_licence", {
        p_org: orgId,
        p_seats: terms.seats,
        p_family_allowance: terms.familyAllowance,
        p_family_price: terms.familyPrice,
        p_family_retail_price: terms.familyRetailPrice,
        p_sst_rate: terms.sstRate,
        p_currency: terms.currency,
        p_billing_email: terms.billingEmail,
        p_contract_end: terms.contractEnd,
      }),
    consoleSetOrgStatus: (orgId, status, endLicences) =>
      callRpc<void>(client, "console_set_org_status", {
        p_org: orgId,
        p_status: status,
        p_end_licences: endLicences,
      }),
    consolePreviewInvoice: async (orgId, periodStart, periodEnd) => {
      const rows = await callRpc<InvoicePreviewRow[] | null>(client, "console_preview_invoice", {
        p_org: orgId,
        p_period_start: periodStart,
        p_period_end: periodEnd,
      });
      const r = rows?.[0];
      return r ? toInvoicePreview(r) : null;
    },
    consoleIssueInvoice: (orgId, input) =>
      callRpc<Id>(client, "console_issue_invoice", {
        p_org: orgId,
        p_period_start: input.periodStart,
        p_period_end: input.periodEnd,
        p_amount: input.amount,
        p_due: input.due,
        p_note: input.note,
      }),
    consoleSetInvoiceStatus: (invoiceId, status) =>
      callRpc<void>(client, "console_set_invoice_status", {
        p_invoice: invoiceId,
        p_status: status,
      }),

    // --- Push (migration 0009) ------------------------------------------------
    notifyFamily: async (input) => {
      const { error } = await client.functions.invoke("notify-family", { body: input });
      if (error) {
        throw new PortalWriteError(
          "notifyFamily",
          error instanceof Error ? error.message : String(error),
        );
      }
    },

    // --- Per-child linking + "Seen at home" -----------------------------------
    setLinkDetails: (childId, viewerAccountId, role, label) =>
      callRpc<void>(client, "set_link_details", {
        p_child: childId,
        p_viewer: viewerAccountId,
        p_role: role,
        p_label: label,
      }),
    childAdults: async (childId) =>
      (
        (await callRpc<ChildAdultRow[] | null>(client, "child_adults", {
          p_child: childId,
        })) ?? []
      ).map(toChildAdult),
    homeNotes: async () => {
      const { data, error } = await client
        .from("home_note")
        .select("id, child_id, skill_id, body, week_of, created_at");
      if (error) throw rpcError("homeNotes", error);
      return ((data ?? []) as unknown as HomeNoteRow[]).map(toHomeNote);
    },
  };
}

// --- 0008 rows + mappers -------------------------------------------------------

/** child_link columns read when the backend has migration 0008. */
export const CHILD_LINK_COLUMNS =
  "child_id, account_id, relation, consent_id, scope, role, label, created_at";

/** Columns `listInvoices` asks for. */
export const INVOICE_COLUMNS =
  "id, org_id, number, period_start, period_end, licence_months, unit_price, subtotal, sst_rate, sst_amount, amount, currency, status, issued_at, due_at, paid_at, note";

/** Columns `consoleLicenceTerms` reads from `license` (0009 adds retail + SST). */
export const LICENCE_TERMS_COLUMNS =
  "org_id, seats_total, family_allowance, family_price_monthly, family_retail_price_monthly, sst_rate, currency, billing_email, contract_end";

/** PostgREST returns `numeric` as a number or, for large values, a string. */
function num(v: unknown): number {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : 0;
  return Number.isFinite(n) ? n : 0;
}

function toLifecycle(v: unknown): OrgLifecycle {
  return v === "suspended" || v === "closed" ? v : "active";
}

const toKind = (v: unknown): OrgKind => (v === "individual" ? "individual" : "organisation");

interface BillingSummaryRow {
  currency: string | null;
  family_price_monthly: number | string | null;
  family_retail_price_monthly?: number | string | null;
  org_share_monthly?: number | string | null;
  sst_rate?: number | string | null;
  family_allowance: number | null;
  licences_live: number | null;
  licences_claimed: number | null;
  licences_pending: number | null;
  month_to_date_licence_months: number | string | null;
  month_to_date_amount: number | string | null;
  projected_month_amount: number | string | null;
  last_month_licence_months: number | string | null;
  last_month_amount: number | string | null;
  outstanding_amount: number | string | null;
  org_status: string | null;
  contract_start: string | null;
  contract_end: string | null;
}

const toBillingSummary = (r: BillingSummaryRow): BillingSummary => ({
  currency: r.currency || "MYR",
  familyPriceMonthly: num(r.family_price_monthly),
  familyRetailPriceMonthly: num(r.family_retail_price_monthly),
  orgShareMonthly: num(r.org_share_monthly),
  sstRate: num(r.sst_rate),
  familyAllowance: num(r.family_allowance),
  licencesLive: num(r.licences_live),
  licencesClaimed: num(r.licences_claimed),
  licencesPending: num(r.licences_pending),
  monthToDateLicenceMonths: num(r.month_to_date_licence_months),
  monthToDateAmount: num(r.month_to_date_amount),
  projectedMonthAmount: num(r.projected_month_amount),
  lastMonthLicenceMonths: num(r.last_month_licence_months),
  lastMonthAmount: num(r.last_month_amount),
  outstandingAmount: num(r.outstanding_amount),
  orgStatus: toLifecycle(r.org_status),
  contractStart: toIsoDate(r.contract_start),
  contractEnd: toIsoDate(r.contract_end),
});

interface InvoiceRow {
  id: string;
  org_id: string;
  number: string;
  period_start: string;
  period_end: string;
  licence_months: number | string;
  unit_price: number | string;
  subtotal?: number | string | null;
  sst_rate?: number | string | null;
  sst_amount?: number | string | null;
  amount: number | string;
  currency: string;
  status: string;
  issued_at: string;
  due_at: string | null;
  paid_at: string | null;
  note: string | null;
}

const toInvoice = (r: InvoiceRow): Invoice => ({
  id: r.id,
  orgId: r.org_id,
  number: r.number,
  periodStart: toIsoDate(r.period_start) ?? r.period_start,
  periodEnd: toIsoDate(r.period_end) ?? r.period_end,
  licenceMonths: num(r.licence_months),
  unitPrice: num(r.unit_price),
  // A pre-0009 row has no subtotal: it was the whole amount, with no SST.
  subtotal: r.subtotal == null ? num(r.amount) : num(r.subtotal),
  sstRate: num(r.sst_rate),
  sstAmount: num(r.sst_amount),
  amount: num(r.amount),
  currency: r.currency || "MYR",
  status: r.status as InvoiceStatus,
  issuedAt: r.issued_at,
  dueAt: toIsoDate(r.due_at),
  paidAt: r.paid_at,
  note: r.note,
});

interface InvoicePreviewRow {
  licence_months: number | string;
  unit_price: number | string;
  subtotal?: number | string | null;
  sst_rate?: number | string | null;
  sst_amount?: number | string | null;
  amount: number | string;
  currency: string | null;
}

const toInvoicePreview = (r: InvoicePreviewRow): InvoicePreview => ({
  licenceMonths: num(r.licence_months),
  unitPrice: num(r.unit_price),
  subtotal: r.subtotal == null ? num(r.amount) : num(r.subtotal),
  sstRate: num(r.sst_rate),
  sstAmount: num(r.sst_amount),
  amount: num(r.amount),
  currency: r.currency || "MYR",
});

interface OrgChildRow {
  child_ref: string;
  supported_by: string[] | null;
  roles: string[] | null;
  sharing_status: string;
  activity: string;
  licence: string;
  archived: boolean | null;
  linked_since: string | null;
  /** 0012. Absent before that migration: reads as not shared, no staff. */
  consent?: string | null;
  staff_count?: number | string | null;
}

const toOrgChildEntry = (r: OrgChildRow): OrgChildEntry => ({
  childRef: r.child_ref,
  supportedBy: r.supported_by ?? [],
  roles: (r.roles ?? []) as LinkRole[],
  sharingStatus: r.sharing_status as SharingStatus,
  activity: (["active", "inactive"].includes(r.activity) ? r.activity : "unknown") as ChildActivity,
  licence: (["licensed", "paid"].includes(r.licence) ? r.licence : "free") as ChildLicence,
  archived: r.archived === true,
  linkedSince: r.linked_since,
  consent: r.consent === "shared" ? "shared" : "not_shared",
  staffCount: num(r.staff_count),
});

interface BulkLicenceRow {
  email: string;
  status: string;
  sponsorship_id: string | null;
}

const BULK_STATUSES: readonly BulkLicenceStatus[] = ["licensed", "already_licensed", "paid", "invalid", "full", "suspended"];

const toBulkLicenceResult = (r: BulkLicenceRow): BulkLicenceResult => ({
  email: r.email,
  // An unknown status is reported as not given, never as given.
  status: (BULK_STATUSES as readonly string[]).includes(r.status) ? (r.status as BulkLicenceStatus) : "invalid",
  sponsorshipId: r.sponsorship_id ?? null,
});

interface OrgChildStaffRow {
  account_id: string;
  email: string | null;
  role: string | null;
  assigned_at: string | null;
}

const toOrgChildStaff = (r: OrgChildStaffRow): OrgChildStaff => ({
  accountId: r.account_id,
  email: r.email ?? r.account_id,
  role: (r.role ?? null) as LinkRole | null,
  assignedAt: r.assigned_at ?? null,
});

/** org_analytics returns one jsonb object with snake_case keys. */
export function toOrgAnalytics(v: Record<string, unknown> | null): OrgAnalytics {
  const o = v ?? {};
  const months = Array.isArray(o.licence_months_by_month) ? o.licence_months_by_month : [];
  return {
    members: num(o.members),
    admins: num(o.admins),
    childrenLinked: num(o.children_linked),
    childrenActive30d: num(o.children_active_30d),
    childrenArchived: num(o.children_archived),
    licencesLive: num(o.licences_live),
    licencesClaimed: num(o.licences_claimed),
    licencesEnding30d: num(o.licences_ending_30d),
    suggestions30d: num(o.suggestions_30d),
    suggestionsSeen30d: num(o.suggestions_seen_30d),
    suggestionsPlayed30d: num(o.suggestions_played_30d),
    licenceMonthsByMonth: (months as { month?: unknown; licence_months?: unknown }[]).map((m) => ({
      month: String(m.month ?? ""),
      licenceMonths: num(m.licence_months),
    })),
  };
}

interface ConsoleOrgRow {
  org_id: string;
  name: string;
  kind: string;
  type: string;
  status: string;
  created_at: string;
  admin_emails: string[] | null;
  members: number | null;
  family_allowance: number | null;
  licences_live: number | null;
  family_price_monthly: number | string | null;
  currency: string | null;
  outstanding: number | string | null;
}

const toConsoleOrg = (r: ConsoleOrgRow): ConsoleOrg => ({
  orgId: r.org_id,
  name: r.name,
  kind: toKind(r.kind),
  type: r.type,
  status: toLifecycle(r.status),
  createdAt: r.created_at,
  adminEmails: r.admin_emails ?? [],
  members: num(r.members),
  familyAllowance: num(r.family_allowance),
  licencesLive: num(r.licences_live),
  familyPriceMonthly: num(r.family_price_monthly),
  currency: r.currency || "MYR",
  outstanding: num(r.outstanding),
});

interface AccessCodeRow {
  code: string;
  label: string | null;
  plan: string | null;
  kind: string | null;
  seats_total: number | null;
  family_allowance: number | null;
  family_price_monthly: number | string | null;
  currency: string | null;
  max_uses: number | null;
  uses: number | null;
  expires_at: string | null;
  created_at: string;
}

const toAccessCode = (r: AccessCodeRow): AccessCode => ({
  code: r.code,
  label: r.label ?? "",
  plan: r.plan ?? "",
  kind: toKind(r.kind),
  seatsTotal: num(r.seats_total),
  familyAllowance: num(r.family_allowance),
  familyPriceMonthly: num(r.family_price_monthly),
  currency: r.currency || "MYR",
  maxUses: num(r.max_uses),
  uses: num(r.uses),
  expiresAt: r.expires_at,
  createdAt: r.created_at,
});

interface ChildAdultRow {
  account_id: string;
  email: string;
  role: string | null;
  label: string | null;
  scope: string[] | null;
  status: string;
  org_name: string | null;
  linked_at: string | null;
}

const toChildAdult = (r: ChildAdultRow): ChildAdult => ({
  accountId: r.account_id,
  email: r.email,
  role: (r.role as LinkRole | null) ?? null,
  label: r.label,
  scope: (r.scope ?? []) as ConsentScopeKey[],
  status: r.status as SharingStatus,
  orgName: r.org_name,
  linkedAt: r.linked_at,
});

interface ChildSuggestionRow {
  id: string;
  batch_id: string | null;
  target: string;
  note: string | null;
  status: string;
  seen_at: string | null;
  sent_at: string;
  from_me: boolean | null;
  sender_role: string | null;
  sender_label: string | null;
  sender_org: string | null;
}

export const toChildSuggestion = (r: ChildSuggestionRow): ChildSuggestion => ({
  id: r.id,
  batchId: r.batch_id ?? null,
  target: r.target,
  note: r.note ?? null,
  status: r.status as AssignmentStatus,
  seenAt: r.seen_at ?? null,
  sentAt: r.sent_at,
  fromMe: r.from_me === true,
  senderRole: (r.sender_role as LinkRole | null) ?? null,
  senderLabel: r.sender_label?.trim() ? r.sender_label.trim() : null,
  senderOrg: r.sender_org?.trim() ? r.sender_org.trim() : null,
});

interface HomeNoteRow {
  id: string;
  child_id: string;
  skill_id: string;
  body: string;
  week_of: string;
  created_at: string;
}

const toHomeNote = (r: HomeNoteRow): HomeNote => ({
  id: r.id,
  childId: r.child_id,
  skillId: r.skill_id,
  body: r.body,
  weekOf: toIsoDate(r.week_of) ?? r.week_of,
  createdAt: r.created_at,
});

export type { OrgProfile };
